import React from 'react';
import classnames from 'classnames';
import ArrowRightIcon from '../../../../../../../../svg/icons/arrow-line-right.svg';
import inputStyles from './input.module.pcss';
import { MessagePropertiesContext } from '../../../../../../contexts/messageProperties';
import mediaQuery from '../../../../../../helpers/mediaQuery';

export const INPUT_ELEMENT_ID = 'search-app-input';

interface InputProps {
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleClear: () => void;
  input: string;
  maxLength: number;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ input, maxLength, handleChange, handleClear }: InputProps, inputRef): React.ReactElement => {
    const [formattedMessage] = React.useContext(MessagePropertiesContext);
    const isSmallOnly = mediaQuery.isSmallOnly();
    const placeholder = isSmallOnly
      ? formattedMessage({
          id: 'search.input.placeholder.mobile',
          fallback: 'Search'
        })
      : formattedMessage({
          id: 'search.input.placeholder',
          fallback: 'Search for a keyword, brand or product'
        });

    return (
      <div className={classnames(inputStyles['input-container'])}>
        <input
          ref={inputRef}
          autoComplete="off"
          aria-label="search bar"
          className={inputStyles.input}
          id={INPUT_ELEMENT_ID}
          type="search"
          name="text"
          value={input}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={handleChange}
          data-click-inside-search
          required
        />
        <div className={inputStyles.input__buttons}>
          <ClearButton input={input} handleClear={handleClear} />
          <SubmitButton input={input} />
        </div>
      </div>
    );
  }
);

function ClearButton({ input, handleClear }: { input: string; handleClear: () => void }): React.ReactElement {
  const [formattedMessage] = React.useContext(MessagePropertiesContext);
  return (
    <>
      {input && (
        <button
          className={classnames(inputStyles.input__clear, 'button--tertiary')}
          type="button"
          onClick={handleClear}
        >
          <span>
            {formattedMessage({
              id: 'search.input.placeholder.clear',
              fallback: 'Clear'
            })}
          </span>
        </button>
      )}
    </>
  );
}

export function SubmitButton({ input, className }: { input: string; className?: string }): React.ReactElement {
  return (
    <button
      disabled={!input}
      className={classnames(
        inputStyles.input__submit,
        className,
        { [inputStyles['input__submit--disabled']]: !input },
        'button--primary'
      )}
      type="submit"
    >
      <ArrowRightIcon className={inputStyles.input__icon} />
    </button>
  );
}

export default Input;
